import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { FileSpreadsheet, CheckCircle2, AlertTriangle, Save, Download, Activity, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export const SHEETS = {
  spreadsheetIdInput: 'sheets-spreadsheet-id-input',
  serviceAccountInput: 'sheets-service-account-input',
  autoSyncSwitch: 'sheets-auto-sync-switch',
  saveButton: 'sheets-save-button',
  testButton: 'sheets-test-button',
  exportButton: 'sheets-export-button',
};

export default function Sheets() {
  const { current, currentId, fetchProjects } = useProject();
  const [spreadsheetId, setSpreadsheetId] = useState("");
  const [serviceAccountJson, setServiceAccountJson] = useState("");
  const [autoSync, setAutoSync] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (current) {
      setSpreadsheetId(current.spreadsheet_id || "");
      setAutoSync(!!current.sheets_auto_sync);
      setServiceAccountJson(""); // never preload — secret
      setTestResult(null);
    }
  }, [current]);

  const save = async () => {
    if (!currentId) return;
    setSaving(true);
    try {
      const body = {
        spreadsheet_id: spreadsheetId,
        auto_sync: autoSync,
      };
      if (serviceAccountJson.trim()) body.service_account_json = serviceAccountJson;
      await api.patch(`/projects/${currentId}/sheets`, body);
      toast.success("Konfigurasi tersimpan");
      setServiceAccountJson("");
      await fetchProjects();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const testConn = async () => {
    if (!currentId) return;
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/projects/${currentId}/sheets/test`);
      setTestResult({ ok: true, msg: `Terhubung ke "${data.spreadsheet_title}"` });
      toast.success("Koneksi Google Sheets OK");
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setTestResult({ ok: false, msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const doExport = async () => {
    if (!currentId) return;
    setExporting(true);
    try {
      const { data } = await api.post(`/projects/${currentId}/sheets/export`);
      const parts = [`${data.appended || 0} baris diekspor`];
      if (data.pruned) parts.push(`${data.pruned} baris lama dibersihkan`);
      toast.success(parts.join(" · "));
      await fetchProjects();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export gagal");
    } finally {
      setExporting(false);
    }
  };

  if (!currentId) return <div className="text-muted-foreground">Pilih proyek terlebih dulu.</div>;

  const hasCredentials = current?.sheets_has_credentials;
  const isConfigured = hasCredentials && current?.spreadsheet_id;

  return (
    <div>
      <PageHeader
        title="Google Sheets Export"
        overline={current?.name}
        description="Arsipkan histori sensor ke Google Spreadsheet. Setiap proyek punya target sheet sendiri."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Status card */}
        <div className="lg:col-span-1 border border-border rounded-lg bg-card p-6 h-fit">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-primary" />
            </div>
            <div>
              <div className="overline text-muted-foreground">Status</div>
              <div className="font-display font-semibold text-lg">
                {isConfigured ? "Terkonfigurasi" : "Belum diatur"}
              </div>
            </div>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Service Account</span>
              {hasCredentials ? (
                <Badge variant="default" className="bg-primary/10 text-primary border-primary/20"><CheckCircle2 className="w-3 h-3 mr-1" /> Tersimpan</Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground">Belum</Badge>
              )}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Spreadsheet ID</span>
              {current?.spreadsheet_id ? (
                <Badge variant="default" className="bg-primary/10 text-primary border-primary/20"><CheckCircle2 className="w-3 h-3 mr-1" /> Diisi</Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground">Belum</Badge>
              )}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Auto-sync</span>
              <Badge variant="outline" className={current?.sheets_auto_sync ? "border-primary text-primary" : "text-muted-foreground"}>
                {current?.sheets_auto_sync ? "AKTIF" : "Nonaktif"}
              </Badge>
            </div>
            {current?.sheets_last_sync && (
              <div className="pt-2 mt-2 border-t border-border text-xs text-muted-foreground">
                Sync terakhir: {new Date(current.sheets_last_sync).toLocaleString()}
              </div>
            )}
          </div>

          {current?.spreadsheet_id && (
            <a
              href={`https://docs.google.com/spreadsheets/d/${current.spreadsheet_id}`}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-flex items-center gap-1 text-xs text-primary underline-offset-4 hover:underline"
            >
              Buka spreadsheet <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>

        {/* Config form */}
        <div className="lg:col-span-2 border border-border rounded-lg bg-card p-6">
          <div className="overline text-muted-foreground mb-4">Konfigurasi</div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ssid">Spreadsheet ID</Label>
              <Input
                id="ssid"
                value={spreadsheetId}
                onChange={(e) => setSpreadsheetId(e.target.value)}
                placeholder="contoh: 1abc...xyz (terlihat di URL spreadsheet)"
                data-testid={SHEETS.spreadsheetIdInput}
              />
              <p className="text-xs text-muted-foreground">
                URL: https://docs.google.com/spreadsheets/d/<span className="font-mono text-foreground">SPREADSHEET_ID</span>/edit
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sa">Service Account JSON {hasCredentials && <span className="text-xs text-muted-foreground">(kosongkan untuk mempertahankan yang sudah ada)</span>}</Label>
              <Textarea
                id="sa"
                value={serviceAccountJson}
                onChange={(e) => setServiceAccountJson(e.target.value)}
                placeholder={hasCredentials ? '••• tersimpan • paste JSON baru untuk mengganti' : '{\n  "type": "service_account",\n  "project_id": "...",\n  "private_key_id": "...",\n  "private_key": "-----BEGIN PRIVATE KEY-----\\n...",\n  "client_email": "...@...iam.gserviceaccount.com",\n  ...\n}'}
                className="font-mono text-xs min-h-[160px]"
                spellCheck={false}
                data-testid={SHEETS.serviceAccountInput}
              />
            </div>

            <div className="flex items-center justify-between p-3 border border-border rounded-md">
              <div>
                <Label htmlFor="autosync" className="cursor-pointer">Auto-sync</Label>
                <p className="text-xs text-muted-foreground mt-1">Otomatis append reading baru ke sheet setiap <strong>3 jam</strong>. Bila jumlah baris melebihi 500.000, baris terlama otomatis dibersihkan (menyisakan 200.000 baris terbaru) untuk mencegah spreadsheet melampaui batas 10 juta sel Google Sheets.</p>
              </div>
              <Switch
                id="autosync"
                checked={autoSync}
                onCheckedChange={setAutoSync}
                data-testid={SHEETS.autoSyncSwitch}
              />
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={save} disabled={saving} data-testid={SHEETS.saveButton}>
                <Save className="w-4 h-4 mr-1" /> {saving ? "Menyimpan…" : "Simpan"}
              </Button>
              <Button variant="outline" onClick={testConn} disabled={testing || !isConfigured} data-testid={SHEETS.testButton}>
                <Activity className="w-4 h-4 mr-1" /> {testing ? "Mengetes…" : "Tes Koneksi"}
              </Button>
              <Button variant="outline" onClick={doExport} disabled={exporting || !isConfigured} data-testid={SHEETS.exportButton}>
                <Download className="w-4 h-4 mr-1" /> {exporting ? "Mengekspor…" : "Export Sekarang"}
              </Button>
            </div>

            {testResult && (
              <Alert variant={testResult.ok ? "default" : "destructive"}>
                {testResult.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                <AlertDescription>{testResult.msg}</AlertDescription>
              </Alert>
            )}
          </div>
        </div>
      </div>

      {/* Guide */}
      <div className="mt-8 border border-border rounded-lg bg-card p-6">
        <div className="overline text-muted-foreground mb-3">Panduan setup Service Account (Google Cloud)</div>
        <ol className="space-y-3 text-sm text-muted-foreground list-decimal list-inside">
          <li>Buka <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer" className="text-primary underline-offset-4 hover:underline">Google Cloud Console</a> → buat/pilih project.</li>
          <li>Menu <span className="font-mono text-foreground">APIs &amp; Services → Library</span>, cari <strong>Google Sheets API</strong>, klik <strong>Enable</strong>.</li>
          <li>Menu <span className="font-mono text-foreground">IAM &amp; Admin → Service Accounts → Create Service Account</span>. Beri nama (mis. <span className="font-mono">cinnabit-sheets</span>), Create.</li>
          <li>Buka service account → tab <strong>Keys → Add Key → Create new key → JSON</strong>. File <span className="font-mono">.json</span> akan diunduh.</li>
          <li>Buka Google Sheet target → klik <strong>Share</strong> → paste email service account (<span className="font-mono">…@…iam.gserviceaccount.com</span>) → role <strong>Editor</strong>.</li>
          <li>Salin Spreadsheet ID dari URL ke field di atas, paste seluruh isi file JSON ke field Service Account, lalu Simpan.</li>
          <li>Klik <strong>Tes Koneksi</strong> untuk memverifikasi, lalu <strong>Export Sekarang</strong> untuk push histori sensor.</li>
        </ol>
      </div>
    </div>
  );
}
